import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/Notifications.css";
import api from "../utils/api";
import { useCenter } from "../context/CenterContext";
import {
  Bell,
  AlertTriangle,
  CheckCircle,
  CalendarCheck,
  Mail,
  Users,
} from "lucide-react";
import { io } from "socket.io-client";

const getIcon = (type) => {
  switch (type) {
    case "message":
      return <Mail size={20} />;
    case "alert":
      return <AlertTriangle size={20} color="#ef4444" />;
    case "success":
      return <CheckCircle size={20} color="#22c55e" />;
    case "reminder":
      return <CalendarCheck size={20} color="#3b82f6" />;
    case "update":
      return <Users size={20} />;
    default:
      return <Bell size={20} />;
  }
};

const Notifications = () => {
  const navigate = useNavigate();
  const { activeCenterId } = useCenter();

  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [archivedNotifications, setArchivedNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actionMessage, setActionMessage] = useState("");
  const [lastArchived, setLastArchived] = useState(null);


  const loadNotifications = async () => {
    try {
      setLoading(true);

      const [activeRes, archivedRes] = await Promise.all([
        api.get("/api/notifications", {
          params: { centerId: activeCenterId || "" },
        }),
        api.get("/api/notifications/archived", {
          params: { centerId: activeCenterId || "" },
        }),
      ]);

      setNotifications(Array.isArray(activeRes.data) ? activeRes.data : []);
      setArchivedNotifications(
        Array.isArray(archivedRes.data) ? archivedRes.data : []
      );
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setNotifications([]);
      setArchivedNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [activeCenterId, activeTab]);

  const filteredNotifications =
    activeTab === "archived"
      ? archivedNotifications
      : activeTab === "all"
        ? notifications
        : notifications.filter((n) => n.type === activeTab);

  const counts = useMemo(
    () => ({
      all: notifications.length,
      alert: notifications.filter((n) => n.type === "alert").length,
      message: notifications.filter((n) => n.type === "message").length,
      reminder: notifications.filter((n) => n.type === "reminder").length,
      archived: archivedNotifications.length,
    }),
    [notifications, archivedNotifications]
  );

  const unreadCount = notifications.filter((n) => n.unread && !n.archived).length;

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL || "");

    socket.on("notifications:new", () => {
      loadNotifications();
    });

    return () => socket.disconnect();
  }, [activeCenterId]);

  useEffect(() => {
    const handleNotificationRefresh = () => {
      loadNotifications();
    };

    window.addEventListener("kuddle:reminders-updated", handleNotificationRefresh);
    window.addEventListener("kuddle:notifications-updated", handleNotificationRefresh);

    return () => {
      window.removeEventListener("kuddle:reminders-updated", handleNotificationRefresh);
      window.removeEventListener("kuddle:notifications-updated", handleNotificationRefresh);
    };
  }, [activeCenterId]);

  useEffect(() => {
    if (!actionMessage) return;

    const timer = setTimeout(() => {
      setActionMessage("");
      setLastArchived(null);
    }, 2500);

    return () => clearTimeout(timer);
  }, [actionMessage]);

  const markAsRead = async (note) => {
    try {
      if (note.unread) {
        await api.patch(`/api/notifications/${note._id}/read`);
      }

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === note._id ? { ...n, unread: false } : n
        )
      );

      if (note.route) {
        navigate(note.route);
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {

      const res = await api.patch("/api/notifications/mark-all-read", {
        centerId: activeCenterId || "",
      });

      setNotifications(res.data);
      setActionMessage("All notifications marked as read.");

      window.dispatchEvent(new Event("kuddle:notifications-updated"));
    } catch (err) {
      console.error("Mark all as read error:", err);
    }
  };

  const archiveNotification = async (id) => {
    try {
      const archivedNote = notifications.find((n) => n._id === id);

      const res = await api.patch(`/api/notifications/${id}/archive`);

      setNotifications((prev) => prev.filter((n) => n._id !== id));

      setArchivedNotifications((prev) => [
        res.data || { ...archivedNote, archived: true },
        ...prev,
      ]);

      setActionMessage("Notification archived.");

      window.dispatchEvent(new Event("kuddle:notifications-updated"));
    } catch (err) {
      console.error("Failed to archive notification:", err);
    }
  };

  const handleUndoArchive = async () => {
    if (!lastArchived?._id) return;

    try {
      const res = await api.patch(`/api/notifications/${lastArchived._id}/unarchive`);

      setNotifications((prev) => [res.data, ...prev]);
      setLastArchived(null);
      setActionMessage("Archived notification restored.");

      window.dispatchEvent(new Event("kuddle:notifications-updated"));
    } catch (err) {
      console.error("Undo archive error:", err);
    }
  };

  const restoreNotification = async (id) => {
    try {
      await api.patch(`/api/notifications/${id}/unarchive`);

      setActionMessage("Notification restored.");
      await loadNotifications();

      window.dispatchEvent(new Event("kuddle:notifications-updated"));
    } catch (err) {
      console.error("Restore notification error:", err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/api/notifications/${id}`);

      setNotifications((prev) =>
        prev.filter((n) => n._id !== id)
      );

      setArchivedNotifications((prev) =>
        prev.filter((n) => n._id !== id)
      );

      setActionMessage("Deleted");

      window.dispatchEvent(
        new Event("kuddle:notifications-updated")
      );
    } catch (err) {
      console.error("Delete notification error:", err);
    }
  };

  const toggleReadStatus = async (note) => {
    try {
      const endpoint = note.unread
        ? `/api/notifications/${note._id}/read`
        : `/api/notifications/${note._id}/unread`;

      const res = await api.patch(endpoint);

      setNotifications((prev) =>
        prev.map((n) => (n._id === note._id ? res.data : n))
      );

      window.dispatchEvent(new Event("kuddle:notifications-updated"));
    } catch (err) {
      console.error("Failed to toggle notification read status:", err);
    }
  };

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1>
          🔔 Notifications
          {unreadCount > 0 && (
            <span className="notification-count-badge">
              {unreadCount}
            </span>
          )}
        </h1>

        <Link to="/settings/notifications" className="settings-btn">
          Notification Preferences
        </Link>
      </div>

      <p className="notifications-subtext">
        Stay informed about system messages and updates.
      </p>

      <div className="notification-nav-row">
        <nav className="notification-nav">
          <button
            className={`nav-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All ({counts.all})
          </button>

          <button
            className={`nav-btn ${activeTab === "alert" ? "active" : ""}`}
            onClick={() => setActiveTab("alert")}
          >
            Alerts ({counts.alert})
          </button>

          <button
            className={`nav-btn ${activeTab === "message" ? "active" : ""}`}
            onClick={() => setActiveTab("message")}
          >
            Messages ({counts.message})
          </button>

          <button
            className={`nav-btn ${activeTab === "reminder" ? "active" : ""}`}
            onClick={() => setActiveTab("reminder")}
          >
            Reminders ({counts.reminder})
          </button>
        </nav>

        <button
          className={`nav-btn ${activeTab === "archived" ? "active" : ""}`}
          onClick={() => setActiveTab("archived")}
        >
          Archived ({counts.archived})
        </button>

        <div className="notification-toolbar-actions">
          {actionMessage && (
            <span className="notification-action-message">
              {actionMessage}
            </span>
          )}

          <button
            type="button"
            className="mark-all-btn"
            onClick={handleMarkAllRead}
          >
            Mark all as read
          </button>
        </div>
      </div>

      <section className="notifications-list">
        {loading ? (
          <div className="empty-state">Loading notifications...</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-state">
            {activeTab === "all" && "You're all caught up 🎉"}
            {activeTab === "alert" && "No alerts right now"}
            {activeTab === "message" && "No new messages"}
            {activeTab === "reminder" && "No reminders scheduled"}
            {activeTab === "archived" && "No archived notifications"}
          </div>
        ) : (
          filteredNotifications.map((note) => (
            <div
              key={note._id}
              className={`notification-card ${note.unread ? "unread" : "read"}`}
              onClick={() => markAsRead(note)}
            >
              <div className="icon">{getIcon(note.type)}</div>

              <div className="content">
                <strong>{note.title}</strong>
                <p>{note.message}</p>
                <span className="time">
                  {note.createdAt
                    ? new Date(note.createdAt).toLocaleString()
                    : ""}
                </span>
              </div>

              <div className="notification-actions">
                <label
                  className="read-checkbox-label"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={!note.unread}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleReadStatus(note)}
                  />
                  <span>{note.unread ? "Unread" : "Read"}</span>
                </label>

                {activeTab === "archived" ? (
                  <div className="notification-btn-group">
                    <button
                      className="restore-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        restoreNotification(note._id);
                      }}
                    >
                      Restore
                    </button>

                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(note._id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : (
                  <div className="notification-btn-group">
                    <button
                      className="archive-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        archiveNotification(note._id);
                      }}
                    >
                      Archive
                    </button>

                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(note._id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
};



export default Notifications;